import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircleIcon, CheckIcon, ChevronLeftIcon, ClockIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

import {
  Button,
  Input,
  RadioCard as DesignRadioCard,
  RadioGroup,
  Spinner,
  Textarea,
} from '../../components/ui';
import {
  PulseMaturityStage,
  PulseOption,
  PulsePerspective,
  PulseRole,
  PulseSkillProfileRequest,
  PulseSubmissionRequest,
  usePublicPulseWorkshop,
  useSubmitPulseResponse,
} from '../../hooks/usePulse';

const TOTAL_STEPS = 6;

interface SurveyDraft {
  role?: PulseRole;
  perspective?: PulsePerspective;
  jobTitle: string;
  aiUsageStage?: number;
  maturity: Record<string, number>;
  friction: string[];
  priorities: string[];
  blockers: string[];
  currentTools: string[];
  otherFriction: string;
  otherReadinessContext: string;
  workflowExample: string;
  skillCount?: PulseSkillProfileRequest['count_bucket'];
  hasNestedSkills?: boolean;
  usedInAutomations?: boolean;
  sharedAcrossTeam?: boolean;
  repeatablyEvaluated?: boolean;
  teamAdoption?: string;
  repositoryStandardization?: string;
  reviewGovernance?: string;
}

const initialDraft: SurveyDraft = {
  jobTitle: '',
  maturity: {},
  friction: [],
  priorities: [],
  blockers: [],
  currentTools: [],
  otherFriction: '',
  otherReadinessContext: '',
  workflowExample: '',
};

function optionsForKeys(options: PulseOption[], keys: string[]): PulseOption[] {
  const allowed = new Set(keys);
  return options.filter(option => allowed.has(option.key));
}

function toggleSelection(
  selected: string[],
  key: string,
  limit?: number,
  exclusiveKey?: string,
): string[] {
  if (selected.includes(key)) return selected.filter(value => value !== key);
  if (key === exclusiveKey) return [key];
  const withoutExclusive = exclusiveKey ? selected.filter(value => value !== exclusiveKey) : selected;
  if (limit && withoutExclusive.length >= limit) return withoutExclusive;
  return [...withoutExclusive, key];
}

function ChoiceButton({
  option,
  selected,
  onClick,
  compact = false,
}: {
  option: PulseOption;
  selected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      aria-label={option.label}
      aria-pressed={selected}
      onClick={onClick}
      className={`h-auto !justify-start gap-3 !whitespace-normal border text-left font-medium focus-visible:ring-eliza-red ${compact ? 'min-h-11 !rounded-xl px-4 py-2.5' : 'min-h-20 w-full !rounded-2xl px-5 py-4'} ${
        selected
          ? '!border-2 !border-eliza-red !bg-[#fff1f5] !text-[#64102d] shadow-[inset_0_0_0_1px_rgba(177,30,76,0.08)] hover:!bg-[#fff1f5]'
          : '!border-[#dfd7cc] !bg-white !text-charcoal hover:!border-[#b11e4c] hover:!bg-[#fff9fb]'
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-eliza-red bg-white text-eliza-red' : 'border-[#b9aa9d] bg-white text-transparent'
        }`}
      >
        <CheckIcon className="h-3.5 w-3.5 stroke-[3]" />
      </span>
      <span className="min-w-0">
        <span className="block font-semibold">{option.label}</span>
        {option.description && !compact && (
          <span className="mt-1 block text-xs font-normal leading-5 text-[#6f665d]">
            {option.description}
          </span>
        )}
      </span>
    </Button>
  );
}

function SurveyRadioCard({
  option,
  selected,
  compact = false,
  leading,
}: {
  option: PulseOption;
  selected: boolean;
  compact?: boolean;
  leading?: React.ReactNode;
}) {
  return (
    <DesignRadioCard
      value={option.key}
      aria-label={option.description ? `${option.label} ${option.description}` : option.label}
      className={`flex items-start gap-3 font-medium !text-charcoal focus-within:ring-2 focus-within:ring-eliza-red focus-within:ring-offset-2 ${
          compact ? 'min-h-11 !rounded-xl !px-4 !py-2.5' : 'min-h-20 !rounded-2xl !px-5 !py-4'
        } ${
          selected
            ? '!border-2 !border-eliza-red !bg-[#fff1f5] !text-[#64102d] shadow-[inset_0_0_0_1px_rgba(177,30,76,0.08)] dark:!border-eliza-red dark:!bg-[#fff1f5] dark:!text-[#64102d]'
            : '!border-[#dfd7cc] !bg-white !text-charcoal hover:!border-[#b11e4c] hover:!bg-[#fff9fb] dark:!border-[#dfd7cc] dark:!bg-white dark:!text-charcoal dark:hover:!border-[#b11e4c] dark:hover:!bg-[#fff9fb]'
        }`}
    >
      {leading}
      <span className="min-w-0 pr-6">
        <span className="block font-semibold">{option.label}</span>
        {option.description && !compact && (
          <span className="mt-1 block text-xs font-normal leading-5 text-[#6f665d]">
            {option.description}
          </span>
        )}
      </span>
    </DesignRadioCard>
  );
}

function SurveyScaleRadio({
  stage,
  selected,
}: {
  stage: PulseMaturityStage;
  selected: boolean;
}) {
  return (
    <DesignRadioCard
      value={String(stage.score)}
      aria-label={`${stage.score}: ${stage.label}. ${stage.description}`}
      className={`flex min-h-11 min-w-0 items-center justify-center !rounded-xl !p-0 text-sm font-bold focus-within:ring-2 focus-within:ring-eliza-red focus-within:ring-offset-2 [&>div.absolute]:hidden ${
          selected
            ? '!border-2 !border-eliza-red !bg-[#fff1f5] !text-[#8a1538] shadow-[inset_0_0_0_1px_rgba(177,30,76,0.08)] dark:!border-eliza-red dark:!bg-[#fff1f5] dark:!text-[#8a1538]'
            : '!border-[#dfd7cc] !bg-white !text-charcoal hover:!border-[#b11e4c] hover:!bg-[#fff9fb] dark:!border-[#dfd7cc] dark:!bg-white dark:!text-charcoal dark:hover:!border-[#b11e4c] dark:hover:!bg-[#fff9fb]'
        }`}
    >
      {stage.score}
    </DesignRadioCard>
  );
}

function SurveyShell({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <main
      className="fixed inset-0 overflow-hidden bg-[#f6f0e8] text-charcoal sm:px-6 sm:py-8"
    >
      <div className="mx-auto flex h-dvh max-w-3xl flex-col overflow-hidden border border-[#ded3c6] bg-[#fffdf9] shadow-[0_16px_48px_rgba(67,49,34,0.08)] sm:h-[calc(100dvh-4rem)] sm:rounded-[28px]">
        <header className="flex shrink-0 items-center justify-between border-b border-[#e7ded3] px-5 py-4 sm:px-8">
          <img src="/pulse/eliza-logo-black.png" alt="Eliza" className="h-6 w-auto" />
          <span className="rounded-full border border-[#ded3c6] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6f665d]">
            PULSE
          </span>
        </header>
        {children}
      </div>
    </main>
  );
}

function SurveyViewport({
  children,
  scrollRef,
  className = '',
}: {
  children: React.ReactNode;
  scrollRef?: React.Ref<HTMLDivElement>;
  className?: string;
}) {
  return (
    <div
      ref={scrollRef}
      data-testid="pulse-survey-scroll-container"
      className={`min-h-0 flex-1 overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable] ${className}`}
    >
      {children}
    </div>
  );
}

function responseStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined;
  const response = error.response;
  if (!response || typeof response !== 'object' || !('status' in response)) return undefined;
  return typeof response.status === 'number' ? response.status : undefined;
}

function responseDetail(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined;
  const response = error.response;
  if (!response || typeof response !== 'object' || !('data' in response)) return undefined;
  const data = response.data;
  if (!data || typeof data !== 'object' || !('detail' in data)) return undefined;
  return typeof data.detail === 'string' ? data.detail : undefined;
}

function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: React.ReactNode;
}) {
  return (
    <SurveyShell>
      <SurveyViewport>
        <div className="flex min-h-full items-center justify-center px-6 py-16 text-center">
          <div className="max-w-md">
            <LockClosedIcon className="mx-auto h-10 w-10 text-eliza-red" />
            <h1 className="mt-5 font-title text-3xl leading-tight">{title}</h1>
            <p className="mt-3 text-sm leading-6 text-[#6f665d]">{message}</p>
            {action && <div className="mt-6">{action}</div>}
          </div>
        </div>
      </SurveyViewport>
    </SurveyShell>
  );
}

export default function PulseSurveyPage() {
  const { organizationSlug = '', workshopKey = '' } = useParams();
  const surveyQuery = usePublicPulseWorkshop(organizationSlug, workshopKey);
  const submission = useSubmitPulseResponse(organizationSlug, workshopKey);
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<SurveyDraft>(initialDraft);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const surveyScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (started && !completed) {
      surveyScrollRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
      stepHeadingRef.current?.focus();
    }
  }, [completed, started, step]);

  const questionnaire = surveyQuery.data?.questionnaire;
  const submissionStatus = responseStatus(submission.error);
  const submissionConflict = submissionStatus === 409;
  const submissionClosed = submissionConflict
    && responseDetail(submission.error)?.toLowerCase().includes('closed');
  const technical = draft.role === 'engineer' || draft.role === 'engineering-leader';
  const branch = questionnaire?.branches[technical ? 'technical' : 'business'];
  const capabilities = useMemo(
    () => questionnaire && branch ? optionsForKeys(questionnaire.capabilities, branch.maturity_keys) : [],
    [branch, questionnaire],
  );
  const frictionOptions = useMemo(
    () => questionnaire && branch ? optionsForKeys(questionnaire.friction, branch.friction_keys) : [],
    [branch, questionnaire],
  );
  const priorityOptions = useMemo(
    () => questionnaire && branch ? optionsForKeys(questionnaire.priorities, branch.priority_keys) : [],
    [branch, questionnaire],
  );

  if (surveyQuery.isLoading) {
    return (
      <SurveyShell>
        <SurveyViewport scrollRef={surveyScrollRef}>
          <div className="flex min-h-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        </SurveyViewport>
      </SurveyShell>
    );
  }
  if (surveyQuery.isError || !surveyQuery.data || !questionnaire) {
    if (responseStatus(surveyQuery.error) === 429) {
      return (
        <EmptyState
          title="Please wait a moment"
          message="This survey received a lot of requests. Wait about a minute, then try again."
          action={(
            <Button
              variant="brand"
              onClick={() => {
                void surveyQuery.refetch();
              }}
            >
              Try again
            </Button>
          )}
        />
      );
    }
    return <EmptyState title="Survey unavailable" message="This link is invalid or no longer active. Ask the workshop facilitator for the current link." />;
  }
  if (surveyQuery.data.status === 'closed') {
    return <EmptyState title="This survey is closed" message="The facilitator is no longer collecting readiness responses for this workshop." />;
  }
  if (step === TOTAL_STEPS - 1 && submissionClosed) {
    return <EmptyState title="This survey is closed" message="The facilitator is no longer collecting readiness responses for this workshop." />;
  }
  if (step === TOTAL_STEPS - 1 && submissionConflict) {
    return (
      <EmptyState
        title="This survey has changed"
        message="The facilitator updated this survey after you opened it. Reload it before submitting a new response."
        action={(
          <Button
            variant="brand"
            onClick={() => {
              setDraft(initialDraft);
              setStep(0);
              setStarted(false);
              void surveyQuery.refetch();
            }}
          >
            Reload survey
          </Button>
        )}
      />
    );
  }
  if (completed) {
    return (
      <SurveyShell>
        <SurveyViewport scrollRef={surveyScrollRef}>
          <div className="flex min-h-full items-center justify-center px-6 py-16 text-center">
            <div className="max-w-md">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-eliza-red" />
              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-eliza-red">Response received</p>
              <h1 className="mt-3 font-title text-3xl leading-tight">Thank you.</h1>
              <p className="mt-3 text-sm leading-6 text-[#6f665d]">Your anonymous input will help the facilitators focus the Codex workshop on the team’s operating reality.</p>
            </div>
          </div>
        </SurveyViewport>
      </SurveyShell>
    );
  }
  if (!started) {
    return (
      <SurveyShell>
        <SurveyViewport scrollRef={surveyScrollRef}>
          <div className="flex min-h-full flex-col justify-center px-6 py-12 sm:px-12 sm:py-16">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-eliza-red">Workshop readiness</p>
            <h1 className="mt-4 max-w-xl font-title text-4xl leading-[1.12] sm:text-5xl">
              {surveyQuery.data.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-[#5f574f]">{surveyQuery.data.purpose}</p>
            <p className="mt-3 flex items-center gap-2 text-sm font-medium text-[#6f665d]">
              <ClockIcon aria-hidden="true" className="h-4 w-4 text-eliza-red" />
              {surveyQuery.data.effort}
            </p>
            <div className="mt-7 flex w-full items-start gap-3 rounded-2xl border border-[#f0c7d4] bg-[#fff4f7] px-4 py-4 text-sm leading-6 text-[#5f293b]">
              <LockClosedIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-eliza-red" />
              <p>{surveyQuery.data.privacy}</p>
            </div>
            <Button className="mt-9 self-start" variant="brand" size="lg" onClick={() => setStarted(true)}>
              Start survey
            </Button>
          </div>
        </SurveyViewport>
      </SurveyShell>
    );
  }

  const advance = () => setStep(current => Math.min(TOTAL_STEPS - 1, current + 1));
  const retreat = () => setStep(current => Math.max(0, current - 1));
  const selectedStage = questionnaire.maturity_stages.find(stage => stage.score === draft.aiUsageStage);
  const skillsRequired = technical && (draft.maturity.skills ?? 0) >= 2;
  const advancedSkills = draft.skillCount === '4-10' || draft.skillCount === '11-plus';
  const leaderRequired = draft.role === 'engineering-leader';
  const adaptiveComplete = (!skillsRequired || Boolean(draft.skillCount))
    && (!skillsRequired || !advancedSkills || [draft.hasNestedSkills, draft.usedInAutomations, draft.sharedAcrossTeam, draft.repeatablyEvaluated].every(value => value !== undefined))
    && (!leaderRequired || Boolean(draft.teamAdoption && draft.repositoryStandardization && draft.reviewGovernance));
  const submissionRateLimited = submissionStatus === 429;

  const chooseRole = (role: PulseRole) => {
    setDraft(current => ({
      ...current,
      role,
      aiUsageStage: undefined,
      maturity: {},
      friction: [],
      priorities: [],
      otherFriction: '',
      skillCount: undefined,
      teamAdoption: undefined,
      repositoryStandardization: undefined,
      reviewGovernance: undefined,
    }));
  };

  const chooseOverallStage = (stage: PulseMaturityStage) => {
    const branchKey = technical ? 'technical' : 'business';
    const maturity = Object.fromEntries(
      questionnaire.branches[branchKey].maturity_keys.map(key => [key, stage.score]),
    );
    setDraft(current => ({ ...current, aiUsageStage: stage.score, maturity }));
  };

  const submit = async () => {
    if (!draft.role || !draft.perspective || draft.aiUsageStage === undefined || !adaptiveComplete) return;
    const payload: PulseSubmissionRequest = {
      questionnaire_version: questionnaire.version,
      role: draft.role,
      submission_perspective: draft.perspective,
      job_title: draft.jobTitle.trim() || null,
      ai_usage_stage: draft.aiUsageStage,
      friction: draft.friction,
      priorities: draft.priorities,
      blockers: draft.blockers,
      maturity: draft.maturity,
      current_tools: draft.currentTools,
      other_friction: draft.otherFriction.trim() || null,
      other_readiness_context: draft.otherReadinessContext.trim() || null,
      workflow_example: draft.workflowExample.trim() || null,
    };
    if (skillsRequired && draft.skillCount) {
      payload.skill_profile = { count_bucket: draft.skillCount };
      if (advancedSkills) {
        payload.skill_profile = {
          ...payload.skill_profile,
          has_nested_skills: draft.hasNestedSkills,
          used_in_automations: draft.usedInAutomations,
          shared_across_team: draft.sharedAcrossTeam,
          repeatably_evaluated: draft.repeatablyEvaluated,
        };
      }
    }
    if (leaderRequired) {
      payload.leader_context = {
        team_adoption_pattern: draft.teamAdoption as string,
        repository_standardization: draft.repositoryStandardization as string,
        approved_tooling_constraints: draft.blockers.filter(value => value !== 'none'),
        review_governance: draft.reviewGovernance as string,
        leadership_emphasis: draft.priorities,
      };
    }
    try {
      await submission.mutateAsync(payload);
      setCompleted(true);
    } catch {
      // React Query retains the mutation error while this draft stays in place.
    }
  };

  const stepContent = (() => {
    if (step === 0) {
      return (
        <>
          <StepHeading ref={stepHeadingRef} eyebrow="About you" title="Tell us which view you’re sharing" description="Select one option in each section. Your job title is optional." />
          <fieldset>
            <legend className="text-sm font-semibold text-charcoal">1. Your role <span className="font-normal text-[#81766c]">— choose one</span></legend>
            <p className="mt-1 text-xs leading-5 text-[#6f665d]">This selects the most relevant technical or business questions.</p>
            <RadioGroup
              aria-label="Your role"
              name="pulse-role"
              value={draft.role ?? ''}
              onValueChange={value => chooseRole(value as PulseRole)}
              className="mt-3 !grid gap-3 sm:grid-cols-2"
            >
              {questionnaire.roles.map(option => (
                <SurveyRadioCard key={option.key} option={option} selected={draft.role === option.key} />
              ))}
            </RadioGroup>
          </fieldset>
          <fieldset className="mt-8">
            <legend className="text-sm font-semibold text-charcoal">2. Answering for <span className="font-normal text-[#81766c]">— choose one</span></legend>
            <p className="mt-1 text-xs leading-5 text-[#6f665d]">Answer from your own workflow or your team’s typical operating reality.</p>
            <RadioGroup
              aria-label="Answering for"
              name="pulse-perspective"
              value={draft.perspective ?? ''}
              onValueChange={value => setDraft(current => ({ ...current, perspective: value as PulsePerspective }))}
              className="mt-3 !grid gap-3 sm:grid-cols-2"
            >
              {questionnaire.perspectives.map(option => (
                <SurveyRadioCard key={option.key} option={option} selected={draft.perspective === option.key} />
              ))}
            </RadioGroup>
          </fieldset>
          <label className="mt-7 block text-sm font-medium">
            Job title <span className="font-normal text-[#81766c]">(optional)</span>
            <Input className="mt-2 bg-white dark:border-[#dfd7cc] dark:bg-white dark:text-charcoal" maxLength={questionnaire.limits.job_title} value={draft.jobTitle} onChange={event => setDraft(current => ({ ...current, jobTitle: event.target.value }))} />
          </label>
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <StepHeading ref={stepHeadingRef} eyebrow="Current use" title="How established is AI in your work today?" description="Pick the stage that best reflects your typical operating reality—not the most advanced experiment." />
          <fieldset>
            <legend className="sr-only">Overall AI maturity</legend>
            <RadioGroup
              aria-label="Overall AI maturity"
              name="pulse-overall-maturity"
              value={draft.aiUsageStage === undefined ? '' : String(draft.aiUsageStage)}
              onValueChange={value => {
                const stage = questionnaire.maturity_stages.find(option => option.score === Number(value));
                if (stage) chooseOverallStage(stage);
              }}
              className="space-y-3"
            >
              {questionnaire.maturity_stages.map(stage => (
                <SurveyRadioCard
                  key={stage.score}
                  option={{ key: String(stage.score), label: stage.label, description: stage.description }}
                  selected={draft.aiUsageStage === stage.score}
                  leading={<span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#e2d8cd] bg-[#fbf7f1] text-xs font-bold text-charcoal">{stage.score}</span>}
                />
              ))}
            </RadioGroup>
          </fieldset>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <StepHeading ref={stepHeadingRef} eyebrow="Capability check" title="Where does each capability sit today?" description={selectedStage ? `We started each capability at ${selectedStage.label}. Change only what differs.` : undefined} />
          <section aria-labelledby="pulse-maturity-scale" className="mb-5 rounded-2xl border border-[#f0c7d4] bg-[#fff7f9] p-4">
            <h2 id="pulse-maturity-scale" className="text-sm font-semibold text-[#64102d]">Capability maturity scale</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-5">
              {questionnaire.maturity_stages.map(stage => (
                <div key={stage.score} className="rounded-xl border border-[#eadbd3] bg-white px-3 py-2.5">
                  <p className="text-xs font-bold text-eliza-red">{stage.score} · {stage.label}</p>
                  <p className="mt-1 text-[11px] leading-4 text-[#6f665d]">{stage.description}</p>
                </div>
              ))}
            </div>
          </section>
          <div className="space-y-4">
            {capabilities.map(option => {
              const currentStage = questionnaire.maturity_stages.find(stage => stage.score === draft.maturity[option.key]);
              return (
                <fieldset key={option.key} className="rounded-2xl border border-[#e2d8cd] bg-white p-4">
                  <legend className="px-1 text-sm font-semibold">{option.label}</legend>
                  {option.description && <p className="mt-1 text-xs leading-5 text-[#6f665d]">{option.description}</p>}
                  <RadioGroup
                    aria-label={`${option.label} maturity`}
                    name={`pulse-capability-${option.key}`}
                    value={draft.maturity[option.key] === undefined ? '' : String(draft.maturity[option.key])}
                    onValueChange={value => setDraft(current => ({
                      ...current,
                      maturity: { ...current.maturity, [option.key]: Number(value) },
                    }))}
                    className="mt-3 !grid grid-cols-5 gap-2"
                  >
                    {questionnaire.maturity_stages.map(stage => (
                      <SurveyScaleRadio
                        key={stage.score}
                        stage={stage}
                        selected={draft.maturity[option.key] === stage.score}
                      />
                    ))}
                  </RadioGroup>
                  {currentStage && (
                    <p className="mt-3 rounded-xl bg-[#fbf7f1] px-3 py-2 text-xs leading-5 text-[#5f574f]" aria-live="polite">
                      <span className="font-semibold text-[#64102d]">{currentStage.score} · {currentStage.label}</span> — {currentStage.description}
                    </p>
                  )}
              </fieldset>
              );
            })}
          </div>
        </>
      );
    }
    if (step === 3) {
      return (
        <>
          <StepHeading ref={stepHeadingRef} eyebrow="Focus" title="Where would a workshop create the most value?" description="Choose up to three friction points, then up to three topics you most want to explore." />
          <MultiSelect title="Current friction" options={frictionOptions} selected={draft.friction} limit={questionnaire.limits.friction} onChange={friction => setDraft(current => ({ ...current, friction }))} />
          <label className="mt-5 block text-sm font-medium">
            Another friction point <span className="font-normal text-[#81766c]">(optional)</span>
            <Textarea
              className="mt-2 min-h-[76px] bg-white dark:border-[#dfd7cc] dark:bg-white dark:text-charcoal"
              maxLength={questionnaire.limits.other_friction}
              placeholder="Briefly add anything the list missed."
              value={draft.otherFriction}
              onChange={event => setDraft(current => ({ ...current, otherFriction: event.target.value }))}
            />
          </label>
          <MultiSelect className="mt-8" title="Workshop wishlist" options={priorityOptions} selected={draft.priorities} limit={questionnaire.limits.priorities} ordered onChange={priorities => setDraft(current => ({ ...current, priorities }))} />
          {technical && (
            <p className="mt-4 rounded-xl border border-[#f0c7d4] bg-[#fff7f9] px-4 py-3 text-xs leading-5 text-[#5f293b]">
              Here, testing and validation means engineering checks such as tests, builds, security scans, and human review—not proofreading business content.
            </p>
          )}
        </>
      );
    }
    if (step === 4) {
      return (
        <>
          <StepHeading ref={stepHeadingRef} eyebrow="Readiness" title="What should the facilitators know upfront?" description="Select any constraints and the tools currently in use." />
          <MultiSelect title="Constraints" options={questionnaire.blockers} selected={draft.blockers} exclusiveKey="none" onChange={blockers => setDraft(current => ({ ...current, blockers }))} />
          <label className="mt-5 block text-sm font-medium">
            Other readiness context <span className="font-normal text-[#81766c]">(optional)</span>
            <Textarea
              className="mt-2 min-h-[76px] bg-white dark:border-[#dfd7cc] dark:bg-white dark:text-charcoal"
              maxLength={questionnaire.limits.other_readiness_context}
              placeholder="Add a constraint, dependency, or concern the list missed."
              value={draft.otherReadinessContext}
              onChange={event => setDraft(current => ({ ...current, otherReadinessContext: event.target.value }))}
            />
          </label>
          <MultiSelect className="mt-8" title="Current tools (optional)" options={questionnaire.current_tools} selected={draft.currentTools} exclusiveKey="none" onChange={currentTools => setDraft(current => ({ ...current, currentTools }))} />
        </>
      );
    }
    return (
      <>
        <StepHeading ref={stepHeadingRef} eyebrow="Final context" title="One last double-click" description="We only ask the follow-ups that apply to your earlier answers." />
        {skillsRequired && (
          <fieldset>
            <legend className="text-sm font-semibold">How many skills have you built?</legend>
            <RadioGroup
              aria-label="Skills built"
              name="pulse-skill-count"
              value={draft.skillCount ?? ''}
              onValueChange={value => setDraft(current => ({
                ...current,
                skillCount: value as PulseSkillProfileRequest['count_bucket'],
              }))}
              className="mt-3 !grid grid-cols-2 gap-2 sm:grid-cols-4"
            >
              {questionnaire.skill_count_options.map(option => (
                <SurveyRadioCard key={option.key} compact option={option} selected={draft.skillCount === option.key} />
              ))}
            </RadioGroup>
            {advancedSkills && (
              <div className="mt-5 space-y-4 rounded-2xl border border-[#e2d8cd] bg-white/75 p-5">
                <BooleanChoice label="Do any use nested skills?" value={draft.hasNestedSkills} onChange={hasNestedSkills => setDraft(current => ({ ...current, hasNestedSkills }))} />
                <BooleanChoice label="Are any used in automations?" value={draft.usedInAutomations} onChange={usedInAutomations => setDraft(current => ({ ...current, usedInAutomations }))} />
                <BooleanChoice label="Are any shared across the team?" value={draft.sharedAcrossTeam} onChange={sharedAcrossTeam => setDraft(current => ({ ...current, sharedAcrossTeam }))} />
                <BooleanChoice label="Are any evaluated repeatably?" value={draft.repeatablyEvaluated} onChange={repeatablyEvaluated => setDraft(current => ({ ...current, repeatablyEvaluated }))} />
              </div>
            )}
          </fieldset>
        )}
        {leaderRequired && (
          <section className={skillsRequired ? 'mt-8' : ''}>
            <h2 className="text-sm font-semibold">Team operating context</h2>
            <SingleSelect className="mt-4" title="Adoption across the team" options={questionnaire.team_adoption_options} selected={draft.teamAdoption} onChange={teamAdoption => setDraft(current => ({ ...current, teamAdoption }))} />
            <SingleSelect className="mt-5" title="Repository standards" options={questionnaire.standardization_options} selected={draft.repositoryStandardization} onChange={repositoryStandardization => setDraft(current => ({ ...current, repositoryStandardization }))} />
            <SingleSelect className="mt-5" title="Review governance" options={questionnaire.review_governance_options} selected={draft.reviewGovernance} onChange={reviewGovernance => setDraft(current => ({ ...current, reviewGovernance }))} />
          </section>
        )}
        <label className={`${skillsRequired || leaderRequired ? 'mt-8' : ''} block text-sm font-semibold`}>
          A workflow you would love to improve <span className="font-normal text-[#81766c]">(optional)</span>
          <Textarea className="mt-2 min-h-[110px] bg-white dark:border-[#dfd7cc] dark:bg-white dark:text-charcoal" maxLength={questionnaire.limits.workflow_example} placeholder="For example: turn a ticket into a tested PR, or produce a weekly client brief from scattered inputs." value={draft.workflowExample} onChange={event => setDraft(current => ({ ...current, workflowExample: event.target.value }))} />
        </label>
        {submission.error && (
          <p className="mt-4 text-sm leading-6 text-red-700" role="alert">
            {submissionRateLimited
              ? 'This survey received a lot of responses. Your answers are still here—wait about a minute, then try submitting again.'
              : 'We could not submit your response. Your answers are still here; please try again.'}
          </p>
        )}
      </>
    );
  })();

  const continueDisabled = (() => {
    if (step === 0) return !draft.role || !draft.perspective;
    if (step === 1) return draft.aiUsageStage === undefined;
    if (step === 2) {
      return capabilities.some(option => draft.maturity[option.key] === undefined);
    }
    if (step === 3) return !draft.friction.length || !draft.priorities.length;
    if (step === 4) return !draft.blockers.length;
    return !adaptiveComplete || submission.isPending;
  })();

  return (
    <SurveyShell>
      <div className="flex min-h-0 flex-1 flex-col">
        <SurveyViewport scrollRef={surveyScrollRef}>
          <div className="flex min-h-full flex-col">
            <div className="px-5 pt-5 sm:px-8">
              <div className="flex items-center justify-between text-xs text-[#81766c]"><span>Step {step + 1} of {TOTAL_STEPS}</span><span>{Math.round(((step + 1) / TOTAL_STEPS) * 100)}%</span></div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#eee5da]"><div className="h-full rounded-full bg-eliza-red transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }} /></div>
            </div>
            <div key={step} className="flex-1 animate-fade-in px-5 pb-7 pt-8 motion-reduce:animate-none sm:px-10 sm:pb-10">{stepContent}</div>
          </div>
        </SurveyViewport>
        <StepActions
          onBack={step === 0 ? undefined : retreat}
          onContinue={step === TOTAL_STEPS - 1 ? submit : advance}
          continueDisabled={continueDisabled}
          continueLabel={
            step === TOTAL_STEPS - 1
              ? submissionRateLimited
                ? 'Try submitting again'
                : 'Submit response'
              : 'Continue'
          }
          disabledHint={step === 0 ? 'Select one role and one answering perspective to continue.' : undefined}
        />
      </div>
    </SurveyShell>
  );
}

const StepHeading = React.forwardRef<HTMLHeadingElement, { eyebrow: string; title: string; description?: string }>(
  ({ eyebrow, title, description }, ref) => (
    <div className="mb-7">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-eliza-red">{eyebrow}</p>
      <h1 ref={ref} tabIndex={-1} className="mt-3 font-title text-3xl leading-tight focus:outline-none sm:text-4xl">{title}</h1>
      {description && <p className="mt-3 max-w-2xl text-sm leading-6 text-[#6f665d]">{description}</p>}
    </div>
  ),
);
StepHeading.displayName = 'StepHeading';

function StepActions({ onBack, onContinue, continueDisabled, continueLabel = 'Continue', disabledHint }: { onBack?: () => void; onContinue: () => void; continueDisabled?: boolean; continueLabel?: string; disabledHint?: string }) {
  return <div data-testid="pulse-step-actions" className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#e7ded3] bg-[#fffdf9] px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 sm:px-10"><div>{onBack ? <Button variant="ghost" className="dark:text-charcoal dark:hover:bg-[#fbf7f1]" onClick={onBack}><ChevronLeftIcon />Back</Button> : continueDisabled && disabledHint ? <p className="max-w-sm text-xs leading-5 text-[#6f665d]">{disabledHint}</p> : <span />}</div><Button variant="brand" onClick={onContinue} disabled={continueDisabled}>{continueLabel}</Button></div>;
}

function MultiSelect({ title, options, selected, onChange, limit, exclusiveKey, ordered, className = '' }: { title: string; options: PulseOption[]; selected: string[]; onChange: (selected: string[]) => void; limit?: number; exclusiveKey?: string; ordered?: boolean; className?: string }) {
  return <section className={className}><div className="flex items-baseline justify-between"><h2 className="text-sm font-semibold">{title}</h2>{limit && <span className="text-xs text-[#81766c]">{selected.length} of {limit}</span>}</div><div className="mt-3 flex flex-wrap gap-2">{options.map(option => { const index = selected.indexOf(option.key); return <ChoiceButton key={option.key} compact option={{ ...option, label: ordered && index >= 0 ? `${index + 1}. ${option.label}` : option.label }} selected={index >= 0} onClick={() => onChange(toggleSelection(selected, option.key, limit, exclusiveKey))} />; })}</div></section>;
}

function radioGroupName(title: string): string {
  return `pulse-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

function SingleSelect({ title, options, selected, onChange, className = '' }: { title: string; options: PulseOption[]; selected?: string; onChange: (selected: string) => void; className?: string }) {
  return <fieldset className={className}><legend className="text-xs font-medium uppercase tracking-wide text-[#81766c]">{title}</legend><RadioGroup aria-label={title} name={radioGroupName(title)} value={selected ?? ''} onValueChange={onChange} className="mt-2 !grid gap-2 sm:grid-cols-2">{options.map(option => <SurveyRadioCard key={option.key} compact option={option} selected={selected === option.key} />)}</RadioGroup></fieldset>;
}

function BooleanChoice({ label, value, onChange }: { label: string; value?: boolean; onChange: (value: boolean) => void }) {
  const name = radioGroupName(label);
  return <fieldset className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center"><legend className="text-sm">{label}</legend><RadioGroup aria-label={label} name={name} value={value === undefined ? '' : value ? 'yes' : 'not-yet'} onValueChange={selection => onChange(selection === 'yes')} className="!grid grid-cols-2 gap-2"><SurveyRadioCard compact option={{ key: 'yes', label: 'Yes' }} selected={value === true} /><SurveyRadioCard compact option={{ key: 'not-yet', label: 'Not yet' }} selected={value === false} /></RadioGroup></fieldset>;
}
